import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, RefreshCw, LifeBuoy, X, Loader2, Users, RotateCcw, CreditCard, ArrowUpCircle } from "lucide-react";
import { Capacitor } from "@capacitor/core";
import { Button } from "@/components/ui/button";
import { useApp } from "@/context/AppContext";
import { storeErrorMessage, markPurchaseFailureInformed, isAlreadyOwnedError, isStoreSideError } from "@/lib/iap";
import { useOutdatedBuild, storeUrl } from "@/lib/appUpdate";
import { openExternal } from "@/lib/externalLinks";

// « Déjà abonné » sur un autre compte Yalola du même téléphone : pas une panne → explication claire + « Restaurer » (si c'est
// bien le membre qui a payé pour lui) + support. Ambre, sans « nous sommes prévenus ».
const AlreadyOwnedCard = ({ failure, onClose, onRestore, t }) => {
  const [restoring, setRestoring] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null); // "ok" | "none"
  const restore = async () => {
    if (!onRestore) return;
    setRestoring(true);
    try {
      const ok = await onRestore();
      setRestoreResult(ok ? "ok" : "none");
    } catch { setRestoreResult("none"); }
    finally { setRestoring(false); }
  };
  const id = failure?.diagnosticId || "";
  const ref = id ? id.slice(0, 8).toUpperCase() : "";
  const subject = t("iap.owned_title", "Abonnement déjà rattaché à un autre compte") + (ref ? ` — Réf. ${ref}` : "");
  const supportTo = `/support?new=1&category=payment&subject=${encodeURIComponent(subject)}`;
  return (
    <div className="rounded-2xl border p-4 space-y-3" style={{ borderColor: "#FCD34D", background: "#FFFBEB" }} role="status" data-testid="purchase-owned-card">
      <div className="flex items-start gap-2">
        <Users size={18} className="mt-0.5 shrink-0" style={{ color: "#B45309" }} />
        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="font-semibold text-sm" data-testid="purchase-owned-title">{t("iap.owned_title", "Abonnement déjà rattaché à un autre compte")}</div>
          <p className="text-xs" style={{ color: "#78350F" }} data-testid="purchase-owned-explain">
            {t("iap.owned_explain", "Un abonnement Yalola Vérifié = un seul compte Yalola. Apple et Google n'autorisent qu'un abonnement actif par compte Apple / Google : celui-ci est déjà utilisé, par exemple par un proche connecté sur ce téléphone.")}
          </p>
          <p className="text-xs" style={{ color: "var(--yalola-text)" }} data-testid="purchase-owned-options">
            {t("iap.owned_options", "Pour abonner ce compte : utilise un autre compte Apple / Google (avec ton moyen de paiement), ou fais la demande depuis le site yalola.app. Si c'est bien toi qui l'as payé pour toi, connecte-toi avec le compte Yalola qui l'a acheté, ou touche « Restaurer mes achats ».")}
          </p>
          <p className="text-xs font-medium flex items-start gap-1.5 rounded-lg px-2 py-1.5" style={{ color: "#78350F", background: "#FEF3C7" }} data-testid="purchase-owned-web-card">
            <CreditCard size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
            <span>{t("iap.owned_web_card", "Tu peux aussi t'abonner directement sur le site yalola.app, par carte bancaire.")}</span>
          </p>
          {restoreResult === "none" && (
            <p className="text-xs font-medium" style={{ color: "#92400E" }} data-testid="purchase-owned-restore-none">{t("iap.owned_restore_none", "Aucun abonnement à ton nom sur ce compte Apple / Google : il appartient à un autre compte Yalola.")}</p>
          )}
        </div>
        <button type="button" onClick={onClose} className="p-1 rounded-full hover:bg-black/5 shrink-0" aria-label={t("common.close", "Fermer")} data-testid="purchase-owned-close"><X size={14} /></button>
      </div>
      <div className="flex flex-wrap gap-2">
        {onRestore && restoreResult !== "ok" && (
          <Button size="sm" variant="outline" className="rounded-full" onClick={restore} disabled={restoring} data-testid="purchase-owned-restore">
            {restoring ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />} <span className="ml-1">{t("iap.owned_restore", "Restaurer mes achats")}</span>
          </Button>
        )}
        <Button asChild size="sm" variant="outline" className="rounded-full">
          <Link to={supportTo} data-testid="purchase-owned-support"><LifeBuoy size={14} /> <span className="ml-1">{t("iap.fail_support", "Contacter le support")}</span></Link>
        </Button>
      </div>
    </div>
  );
};

// Shown in place of the error toast when a store purchase fails (never on cancel): the member knows the team has been
// alerted (see routes_iap.alert_iap_failure), can retry the same purchase, or open Support with subject + reference prefilled.
export const PurchaseFailedCard = ({ failure, onRetry, onClose, busy, onRestore }) => {
  const { t, config } = useApp();
  const id = failure?.diagnosticId || "";
  const ref = id ? id.slice(0, 8).toUpperCase() : "";
  const cause = storeErrorMessage(failure?.error, t);
  // Erreur côté boutique + app installée plus ancienne que celle du store → 1re piste : mettre à jour (APK hors Play, build retiré).
  const outdated = useOutdatedBuild(config?.settings?.app_release);
  const showUpdate = !!outdated && isStoreSideError(failure?.error);
  const openStore = () => { openExternal(storeUrl(config?.settings, Capacitor.getPlatform())).catch(() => {}); };
  useEffect(() => { if (id) markPurchaseFailureInformed(id); }, [id]);
  if (isAlreadyOwnedError(failure?.error)) return <AlreadyOwnedCard failure={failure} onClose={onClose} onRestore={onRestore} t={t} />;
  const subject = t("iap.fail_support_subject", "Achat en échec dans l'app") + (ref ? ` — Réf. ${ref}` : "");
  const message = t("iap.fail_support_message", "Mon achat n'a pas abouti. Cause affichée : {cause}").replace("{cause}", cause) + (ref ? `\n${t("iap.fail_ref", "Référence")} : ${ref}` : "");
  const supportTo = `/support?new=1&category=payment&subject=${encodeURIComponent(subject)}&message=${encodeURIComponent(message)}`;
  return (
    <div className="rounded-2xl border p-4 space-y-3" style={{ borderColor: "#FCA5A5", background: "#FEF2F2" }} role="alert" data-testid="purchase-failed-card">
      <div className="flex items-start gap-2">
        <AlertTriangle size={18} className="mt-0.5 shrink-0" style={{ color: "#DC2626" }} />
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-sm" data-testid="purchase-failed-title">{t("iap.fail_title", "Achat impossible pour le moment")}</div>
          <div className="text-xs mt-0.5" style={{ color: "#7F1D1D" }} data-testid="purchase-failed-notified">{t("iap.fail_notified", "Nous sommes prévenus et regardons ce qui bloque.")}</div>
          <div className="text-xs mt-1" style={{ color: "var(--yalola-text-muted)" }} data-testid="purchase-failed-cause">{cause}{ref && <span className="ml-1 font-mono" data-testid="purchase-failed-ref">· {t("iap.fail_ref", "Référence")} {ref}</span>}</div>
          {showUpdate && (
            <p className="text-xs font-medium flex items-start gap-1.5 rounded-lg px-2 py-1.5 mt-2" style={{ color: "#9A3412", background: "#FFF7ED" }} data-testid="purchase-failed-outdated">
              <ArrowUpCircle size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
              <span>{t("iap.fail_outdated", "Ta version de Yalola est ancienne : la boutique refuse souvent les achats depuis une app qui n'est pas à jour. Mets-la à jour depuis le store (version {version}) puis réessaie.").replace("{version}", outdated.version || "")}</span>
            </p>
          )}
        </div>
        <button type="button" onClick={onClose} className="p-1 rounded-full hover:bg-black/5 shrink-0" aria-label={t("common.close", "Fermer")} data-testid="purchase-failed-close"><X size={14} /></button>
      </div>
      <div className="flex flex-wrap gap-2">
        {showUpdate && (
          <Button size="sm" className="pill-primary rounded-full" onClick={openStore} data-testid="purchase-failed-update">
            <ArrowUpCircle size={14} /> <span className="ml-1">{t("app.update_now", "Mettre à jour")}</span>
          </Button>
        )}
        <Button size="sm" className={`rounded-full ${showUpdate ? "" : "pill-primary"}`} variant={showUpdate ? "outline" : undefined} onClick={onRetry} disabled={!!busy} data-testid="purchase-failed-retry">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} <span className="ml-1">{t("iap.fail_retry", "Réessayer")}</span>
        </Button>
        <Button asChild size="sm" variant="outline" className="rounded-full">
          <Link to={supportTo} data-testid="purchase-failed-support"><LifeBuoy size={14} /> <span className="ml-1">{t("iap.fail_support", "Contacter le support")}</span></Link>
        </Button>
      </div>
    </div>
  );
};
